import styles from "./CreatePostCard.module.scss";
import { useInView } from "react-intersection-observer";
import Card from "@/components/Card";
import { usePersonalDetailsQuery } from "@/fetch/profiles";
import Avatar from "@/components/Avatar";
import Typography from "@/components/Typography";
import Button, { IconButton } from "@/components/Button";
import { PictureIcon, VideoIcon, PlusIcon } from "@/components/Icon";
import { Skeleton } from "@/components/Loader";
import CreatePostDialog from "./CreatePostDialog";
import CreatePostMobileDialog from "./CreatePostMobileDialog";
import { useDialog, useResponsive, useTripType } from "@/hooks";
import cx from "classnames";
import { useState } from "react";

const CreatePostCard: React.FC<{
  className?: string;
  onCreatePost?: () => Promise<any>;
}> = ({ className, onCreatePost }) => {
  const { data: personalDetailsData, isLoading: isPersonalDetailsLoading } =
    usePersonalDetailsQuery();
  const { isMobile } = useResponsive();
  const { isWHArrivalOnly, isSuperliteTrip } = useTripType();
  const profilePicturePath = personalDetailsData?.traveller?.s3_profile_pic;
  const nickname = personalDetailsData?.traveller.nickname || "";
  const disabled = isPersonalDetailsLoading;
  const [mediaPickerInitiallyOpen, setMediaPickerInitiallyOpen] =
    useState(false);

  const { ref: createPostCardRef, inView: isCreatePostCardInView } = useInView({
    threshold: 0,
  });

  const {
    onClose,
    openDialog: openCreatePostDialog,
    isOpen: isCreatingPostDialogOpen,
  } = useDialog("new-post");

  const handleOpenNewPostDialog = () => {
    openCreatePostDialog();
  };

  const onMediaPickerButtonsClick = () => {
    setMediaPickerInitiallyOpen(true);
    openCreatePostDialog();
  };

  const onDialogClose = () => {
    setMediaPickerInitiallyOpen(false);
    onClose();
  };

  return (
    <>
      {isMobile ? (
        <div
          className={styles.container}
          ref={createPostCardRef}
          onClick={handleOpenNewPostDialog}
        >
          {isPersonalDetailsLoading ? (
            <Skeleton variant="circular" width="48px" height="48px" />
          ) : (
            <Avatar cloudinaryPath={profilePicturePath} />
          )}
          <div className={styles.details}>
            {isPersonalDetailsLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Typography color="text.secondary" variant="body2">
                {nickname}
              </Typography>
            )}
            <Typography variant="h6">What&apos;s going on today?</Typography>
          </div>
        </div>
      ) : (
        <Card className={cx(styles.rootCard, className)}>
          {isPersonalDetailsLoading ? (
            <Skeleton variant="circular" width="48px" height="48px" />
          ) : (
            <Avatar cloudinaryPath={profilePicturePath} />
          )}
          <div className={styles.content}>
            <Typography
              variant="h6"
              color={disabled ? "action.disabled" : "text.secondary"}
              component="button"
              tabIndex={1}
              className={styles.title}
              onClick={handleOpenNewPostDialog}
              disabled={disabled}
            >
              What&apos;s going on today?
            </Typography>
            <IconButton
              color="primary"
              disabled={disabled}
              onClick={onMediaPickerButtonsClick}
            >
              <VideoIcon />
            </IconButton>
            <IconButton
              color="primary"
              disabled={disabled}
              onClick={onMediaPickerButtonsClick}
            >
              <PictureIcon />
            </IconButton>
          </div>
        </Card>
      )}
      {isMobile && !isCreatePostCardInView && (
        <Button
          className={cx(styles.floatButton, {
            [styles.rightFloatButton]: isSuperliteTrip || isWHArrivalOnly,
          })}
          onClick={handleOpenNewPostDialog}
        >
          <PlusIcon />
        </Button>
      )}
      {isCreatingPostDialogOpen && (
        <>
          {isMobile ? (
            <CreatePostMobileDialog
              onClose={onDialogClose}
              onCreatePost={onCreatePost}
            />
          ) : (
            <CreatePostDialog
              onClose={onDialogClose}
              onCreatePost={onCreatePost}
              mediaPickerInitiallyOpen={mediaPickerInitiallyOpen}
            />
          )}
        </>
      )}
    </>
  );
};
export default CreatePostCard;
